import { useState, type FC, type ReactNode } from 'react';
import { IconButton, Stack } from '@carbon/react';
import { CheckmarkFilled, ChevronDown } from '@carbon/icons-react';

import styles from './Secao.module.scss';

// UMA SEÇÃO DE PÁGINA (configurações, ficha): o título `h2`, uma frase que diz para que serve e o
// conteúdo. Entre uma seção e outra, o filete do tema.
//
// `aoSair`: a seção que salva sozinha (a ficha da negociação, a do show) grava quando o foco SAI
// dela inteira, e não a cada tecla: cada gravação passa pela RLS, e uma por tecla é rajada.
//
// `passo`: a seção que é um passo de um roteiro (os blocos do show) leva o número numa caixa azul
// antes do título, o guia visual de que há um passo a passo. O leitor de tela ouve "Passo 1:".
//
// `recolhivel`: a seção que encolhe até o título (os doze blocos do show, decidido na call de
// 29/09: com todos abertos a página era um paredão). O chevron fica no fim da linha do título, como
// no Accordion do Carbon, que não serve aqui porque a seção tem ação própria ao lado do título (e
// botão dentro de botão não existe). Recolhida, o conteúdo sai da tela mas continua montado: o que
// foi digitado não se perde. Quem leva o artista a um bloco (a tarefa do show) abre a seção pelo
// `data-alternar-secao`, conferindo antes o `data-recolhida` (`abrirSecao`).
//
// `situacao` e `feito`: a seção que é um passo de um caminho (a ficha da negociação, o show) diz no
// cabeçalho quanto já foi preenchido ("2 de 4") e, completa, troca o texto pelo check verde e a
// caixa do número fica verde. Fica à vista mesmo recolhida: é o que mostra o que falta sem abrir.

/**
 * Leva até a seção: abre (se estiver recolhida), rola até o título e põe o foco no primeiro campo.
 * O foco espera o quadro seguinte, quando o conteúdo aberto já existe.
 */
export const abrirSecao = (id: string) => {
  const titulo = document.getElementById(id);
  const secao = titulo?.closest('section');
  if (!titulo || !secao) return;
  if (secao.dataset.recolhida === 'true') secao.querySelector<HTMLElement>('[data-alternar-secao]')?.click();
  titulo.scrollIntoView({ behavior: 'smooth', block: 'start' });
  requestAnimationFrame(() => {
    const campo = document.getElementById(`${id}-conteudo`)?.querySelector<HTMLElement>('input:not([readonly]), textarea:not([readonly]), button');
    campo?.focus({ preventScroll: true });
  });
};

/**
 * `abrirSecao` para a seção que ainda vai existir: a ficha que acabou de ser pedida (`?n=`) carrega
 * e só então tem a seção. Tenta por uns segundos e desiste calada. A primeira espera deixa o modal
 * que pediu fechar e devolver o foco antes, senão o foco devolvido tiraria o do campo.
 */
export const abrirSecaoQuandoExistir = (id: string, tentativas = 40, espera = 400) => {
  // 400 ms: o modal do Carbon fecha em ~240 ms e devolve o foco no fim, e esse foco cortava a
  // rolagem suave no meio do caminho.
  window.setTimeout(() => {
    if (document.getElementById(id)) abrirSecao(id);
    else if (tentativas > 0) abrirSecaoQuandoExistir(id, tentativas - 1, 120);
  }, espera);
};

export const Secao: FC<{
  titulo: string;
  texto?: ReactNode;
  id?: string;
  /** O que fica à direita do título (uma ação da seção). */
  acao?: ReactNode;
  aoSair?: () => void;
  /** O número do passo, quando a seção é um passo de um roteiro. */
  passo?: number;
  /** A seção encolhe até o título, por um chevron no fim da linha. */
  recolhivel?: boolean;
  /** Com `recolhivel`: se ela abre aberta. O padrão é recolhida. */
  abertaDeInicio?: boolean;
  /** Quanto do passo já foi preenchido ("2 de 4", "Opcional"). */
  situacao?: string;
  /** O passo está completo: o check verde no lugar da situação. */
  feito?: boolean;
  children: ReactNode;
}> = ({ titulo, texto, id, acao, aoSair, passo, recolhivel, abertaDeInicio = false, situacao, feito, children }) => {
  const [aberta, setAberta] = useState(!recolhivel || abertaDeInicio);
  const idDoConteudo = id ? `${id}-conteudo` : undefined;
  return (
    <section
      className={styles.secao}
      aria-labelledby={id}
      data-recolhida={recolhivel ? String(!aberta) : undefined}
      onBlur={aoSair ? (e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) aoSair(); } : undefined}
    >
      <Stack gap={5}>
        {/* Recolhível, a linha inteira do título abre e fecha (pedido do Lucas, 03/10/2026: só o
            chevron era alvo pequeno demais). O chevron continua sendo o controle do teclado e do
            leitor de tela; o clique num botão ou link da linha (a ação da seção) não alterna. */}
        <div
          className={recolhivel ? `${styles.cabeca} ${styles.cabecaClicavel}` : styles.cabeca}
          onClick={recolhivel ? (e) => {
            if ((e.target as HTMLElement).closest('button, a, input, [role="menu"]')) return;
            setAberta((a) => !a);
          } : undefined}
        >
          <Stack gap={3}>
            <h2 id={id} className={styles.titulo}>
              {passo != null && (
                <>
                  <span className={feito ? styles.passoFeito : styles.passo} aria-hidden>{passo}</span>
                  <span className='cds--visually-hidden'>{`Passo ${passo}: `}</span>
                </>
              )}
              {titulo}
            </h2>
            {texto && aberta && <p className={styles.texto}>{texto}</p>}
          </Stack>
          <div className={styles.acoes}>
            {aberta && acao}
            {feito ? (
              <span className={styles.feito}>
                <CheckmarkFilled size={16} aria-hidden='true' />
                Completo
              </span>
            ) : situacao && <span className={styles.situacao}>{situacao}</span>}
            {recolhivel && (
              <IconButton
                kind='ghost'
                size='md'
                align='left'
                label={aberta ? `Recolher ${titulo}` : `Abrir ${titulo}`}
                aria-expanded={aberta}
                aria-controls={idDoConteudo}
                data-alternar-secao
                className={aberta ? styles.chevronAberto : styles.chevron}
                onClick={() => setAberta((a) => !a)}
              >
                <ChevronDown />
              </IconButton>
            )}
          </div>
        </div>
        {recolhivel ? <div id={idDoConteudo} hidden={!aberta} className={styles.conteudo}>{children}</div> : children}
      </Stack>
    </section>
  );
};
